"use client";

import { useCallback } from "react";
import {
  showErrorToast,
  showSuccessToast,
} from "@/shared/feedback/toast";
import type {
  MindstringPrototype,
  PrintSettings,
  SupportedLanguage,
  UpdatePrintSettingsRequest,
} from "@/generated/fastapi";
import { useUserProfile } from "@/state/globalStore";
import useResetPrintSettingsMutation from "../mutations/useResetPrintSettingsMutation";
import useUpdatePrintSettingsMutation from "../mutations/useUpdatePrintSettingsMutation";

const TOAST_SUCCESS_TIMEOUT = 800;

export function getRelevantPrintSettingsConfig(
  printSettings: PrintSettings,
  language: SupportedLanguage,
) {
  const allNames = printSettings.custom_section_names || {};
  const langNames = allNames[language] || {};

  return {
    job_activity_naming: langNames.job_activity || {
      text: null,
      enabled: false,
    },
    learning_outcome_naming: langNames.learning_outcome || {
      text: null,
      enabled: false,
    },
    workplace_characteristic_naming: langNames.workplace_characteristic || {
      text: null,
      enabled: false,
    },
    individual_description_naming: langNames.individual_description || {
      text: null,
      enabled: false,
    },
  };
}

export default function usePrintSettingsActions() {
  const { userProfile, updateUserProfilePrintSettings } = useUserProfile();
  const updateMutation = useUpdatePrintSettingsMutation();
  const resetMutation = useResetPrintSettingsMutation();

  const updatePrintSettingsSync = useCallback(
    async (updates: Partial<PrintSettings>): Promise<PrintSettings | null> => {
      if (!updates || Object.keys(updates).length === 0) return null;

      const currentData = userProfile.data.print_settings;
      const backup = updateUserProfilePrintSettings(currentData);

      try {
        const request: UpdatePrintSettingsRequest = {
          schema_version: currentData.schema_version,
          timestamp_resume:
            updates.timestamp_resume ?? currentData.timestamp_resume,
          sign_resume: updates.sign_resume ?? currentData.sign_resume,
          link_moveon: updates.link_moveon ?? currentData.link_moveon,
          publish_for_days:
            updates.publish_for_days ?? currentData.publish_for_days,
          timestamp_moveon_link:
            updates.timestamp_moveon_link ?? currentData.timestamp_moveon_link,
          primary_color: updates.primary_color ?? currentData.primary_color,
          return_format: updates.return_format ?? currentData.return_format,
          visually_emphatize:
            updates.visually_emphatize ?? currentData.visually_emphatize,
          timeline_framing:
            updates.timeline_framing ?? currentData.timeline_framing,
          date_detail: updates.date_detail ?? currentData.date_detail,
          date_convention: updates.date_convention ?? currentData.date_convention,
          print_language: updates.print_language ?? currentData.print_language,
          section_naming: updates.section_naming ?? currentData.section_naming,
          custom_section_names:
            updates.custom_section_names ?? currentData.custom_section_names,
        };

        const result = await updateMutation.mutateAsync(request);
        updateUserProfilePrintSettings(result);
        showSuccessToast(
          "Print settings updated successfully",
          TOAST_SUCCESS_TIMEOUT,
        );
        return result;
      } catch {
        updateUserProfilePrintSettings(backup);
        showErrorToast("Failed to update print settings");
        return null;
      }
    },
    [updateMutation, updateUserProfilePrintSettings, userProfile.data.print_settings],
  );

  const resetPrintSettingsSync = useCallback(async (): Promise<PrintSettings | null> => {
    const backup = updateUserProfilePrintSettings(userProfile.data.print_settings);

    try {
      const response = await resetMutation.mutateAsync();
      if (!response.reset_data) {
        throw new Error("Missing reset data");
      }

      updateUserProfilePrintSettings(response.reset_data);
      showSuccessToast(
        "Print settings reset to defaults",
        TOAST_SUCCESS_TIMEOUT,
      );
      return response.reset_data;
    } catch {
      updateUserProfilePrintSettings(backup);
      showErrorToast("Failed to reset print settings");
      return null;
    }
  }, [resetMutation, updateUserProfilePrintSettings, userProfile.data.print_settings]);

  const updateCustomSection = useCallback(
    async (
      sectionKey: MindstringPrototype,
      patch: Partial<{ text: string | null; enabled: boolean }> = {},
      language?: SupportedLanguage,
    ) => {
      const currentData = userProfile.data.print_settings;
      const lang = language ?? currentData.print_language;

      const base = (getRelevantPrintSettingsConfig(currentData, lang) as Record<
        string,
        { text: string | null; enabled: boolean }
      >)[`${sectionKey}_naming`] || { text: null, enabled: false };

      const newSection = { ...base, ...(patch || {}) };

      const newCustomSectionNames = {
        ...(currentData.custom_section_names || {}),
        [lang]: {
          ...(currentData.custom_section_names?.[lang] || {}),
          [sectionKey]: newSection,
        },
      };

      return updatePrintSettingsSync({
        custom_section_names: newCustomSectionNames,
      });
    },
    [updatePrintSettingsSync, userProfile.data.print_settings],
  );

  const setCustomSectionEnabled = useCallback(
    (
      sectionKey: MindstringPrototype,
      enabled: boolean,
      language?: SupportedLanguage,
    ) => updateCustomSection(sectionKey, { enabled }, language),
    [updateCustomSection],
  );

  return {
    updatePrintSettingsSync,
    resetPrintSettingsSync,
    updateCustomSection,
    setCustomSectionEnabled,
    isUpdating: updateMutation.isPending,
    isResetting: resetMutation.isPending,
  };
}
